import { useCallback, useEffect, useRef, useState } from 'react';
import { SHEET_SELECTOR } from './use-sheet-swipe-down';

/**
 * What the phone shows over its conversation: the drawer, what the drawer pushed, the Summary
 * page, and the sheets (every menu, popover, dialog and sheet open on the screen).
 */
export type MobileLayer = 'drawer' | 'apps' | 'notifications' | 'search' | 'summary' | 'sheet';

const KEY = 'crowdex.mobile.layers';
const LAYERS: readonly MobileLayer[] = [
  'drawer',
  'apps',
  'notifications',
  'search',
  'summary',
  'sheet',
];

/** The layers a history entry shows (`history.state`). */
export function layersOf(state: unknown): MobileLayer[] {
  const stored = (state as Record<string, unknown> | null)?.[KEY];
  return Array.isArray(stored)
    ? stored.filter((layer): layer is MobileLayer => LAYERS.includes(layer as MobileLayer))
    : [];
}

/**
 * Runs `then` once the phone's layers are left (at once where there are none, as on a desktop):
 * a page pushed while `leave` still goes back would be taken back with them.
 */
export function afterLayers(then: () => void): void {
  if (layersOf(window.history.state).length === 0) {
    then();
    return;
  }
  const onPop = () => {
    if (layersOf(window.history.state).length > 0) return;
    window.removeEventListener('popstate', onPop);
    then();
  };
  window.addEventListener('popstate', onPop);
}

/** The screen under the sheets: the last layer that is not a sheet. */
export function screenLayer(layers: readonly MobileLayer[]): MobileLayer | null {
  return layers.filter((layer) => layer !== 'sheet').at(-1) ?? null;
}

/** How many sheets the history holds on top of its screens. */
export function sheetsOnTop(layers: readonly MobileLayer[]): number {
  let count = 0;
  for (let index = layers.length - 1; index >= 0 && layers[index] === 'sheet'; index -= 1)
    count += 1;
  return count;
}

/** What a sheet is on a phone: a menu, a popover, a select, a dialog, an app's own sheet. */
const HISTORY_SHEET_SELECTOR = [
  SHEET_SELECTOR,
  '[data-slot="dialog-content"]',
  '[data-slot="context-menu-content"]',
].join(', ');

/**
 * The sheets open in `root`: not one closing (still animating out), nor one kept mounted out of
 * sight (`hidden`, `display: none`).
 */
export function openSheetCount(root: ParentNode): number {
  let count = 0;
  for (const element of root.querySelectorAll(HISTORY_SHEET_SELECTOR))
    if (
      element.getAttribute('data-state') !== 'closed' &&
      element.closest('[hidden]') === null &&
      (element.checkVisibility?.() ?? true)
    )
      count += 1;
  return count;
}

/**
 * What to do to bring the history in line with the sheets on screen: push an entry per new
 * sheet, go back past the entries of sheets that closed by themselves.
 */
export function sheetChange(layers: readonly MobileLayer[], open: number): number {
  return open - sheetsOnTop(layers);
}

/** How long after a history move the sheets are left to settle before they are counted. */
const SETTLE_MS = 350;

/**
 * The phone's layers as history entries, so its back button (and a swipe back) closes the one
 * on top, as a phone's own apps do: opening one pushes an entry, `pop` goes back one, `leave`
 * goes back past all of them and then does what was chosen (a conversation, an app, a page),
 * once the history is where it started — its own entry is never popped by a later back.
 *
 * With `sheets`, every sheet opened on the screen (a menu, the + sheet, Model, Tools, ⋮, a
 * dialog) is an entry too: the phone's back closes the sheet, not the app; a sheet closed by
 * itself (its choice, its ×, a swipe down) takes its entry with it.
 */
export function useMobileLayers({ sheets = false }: { sheets?: boolean } = {}) {
  const [layers, setLayers] = useState<MobileLayer[]>(() => layersOf(window.history.state));
  const pending = useRef<(() => void) | null>(null);
  // A `leave` on its way back: another one joins it rather than going back twice as far.
  const leaving = useRef(false);
  const current = useRef(layers);
  current.current = layers;
  // A move of the history asked for and not arrived yet: the sheets are not counted meanwhile.
  const settlingUntil = useRef(0);
  const reconcile = useRef<() => void>(() => undefined);

  const travel = useCallback((delta: number) => {
    settlingUntil.current = performance.now() + SETTLE_MS;
    window.history.go(delta);
  }, []);

  useEffect(() => {
    const sync = () => {
      const before = current.current;
      const next = layersOf(window.history.state);
      current.current = next;
      setLayers(next);
      // The back closed a sheet still on screen: close it as its Escape would.
      if (
        sheets &&
        sheetsOnTop(next) < sheetsOnTop(before) &&
        openSheetCount(document) > sheetsOnTop(next)
      ) {
        settlingUntil.current = performance.now() + SETTLE_MS;
        document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
      } else settlingUntil.current = 0;
      window.setTimeout(() => reconcile.current(), SETTLE_MS);
      if (next.length > 0) return;
      leaving.current = false;
      const then = pending.current;
      pending.current = null;
      then?.();
    };
    window.addEventListener('popstate', sync);
    return () => window.removeEventListener('popstate', sync);
  }, [sheets]);

  const pushAll = useCallback((added: readonly MobileLayer[]) => {
    if (added.length === 0) return;
    let next = current.current;
    for (const layer of added) {
      next = [...next, layer];
      window.history.pushState({ ...(window.history.state as object | null), [KEY]: next }, '');
    }
    current.current = next;
    setLayers(next);
  }, []);

  const push = useCallback(
    (layer: MobileLayer) => {
      if (current.current.at(-1) === layer && layer !== 'sheet') return;
      pushAll([layer]);
    },
    [pushAll],
  );

  const pop = useCallback(() => {
    if (current.current.length > 0) travel(-1);
  }, [travel]);

  const leave = useCallback(
    (then?: () => void) => {
      const depth = current.current.length;
      if (depth === 0) {
        then?.();
        return;
      }
      if (leaving.current) {
        const before = pending.current;
        pending.current = () => {
          before?.();
          then?.();
        };
        return;
      }
      leaving.current = true;
      pending.current = then ?? null;
      travel(-depth);
    },
    [travel],
  );

  // The sheets on screen, kept in line with the history's entries.
  useEffect(() => {
    if (!sheets) return;
    let frame = 0;
    // Checked once a frame: a menu that closes as the sheet it chose opens is one change.
    const check = () => {
      frame = 0;
      if (performance.now() < settlingUntil.current) return;
      const change = sheetChange(current.current, openSheetCount(document));
      if (change > 0) pushAll(Array.from({ length: change }, () => 'sheet' as const));
      else if (change < 0) travel(change);
    };
    const schedule = () => {
      if (frame === 0) frame = window.requestAnimationFrame(check);
    };
    reconcile.current = schedule;
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-state', 'data-sheet', 'hidden', 'class'],
    });
    schedule();
    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(frame);
      reconcile.current = () => undefined;
    };
  }, [pushAll, sheets, travel]);

  return { layers, push, pop, leave };
}
